import { useTenantMemberships } from "@/contexts/TenantContext";
import { LazoPaths } from "@/enums/LazoPaths";
import { Spinner } from "@/external/ensolvers-core-frontend-mui-v5/components/Common/Spinner";
import { TenantMembership } from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/TenantMembership";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import { LazoCompany } from "@/models/LazoCompany";
import { Workspace } from "@/models/LazoWorkspace";
import LazoCompanyService from "@/services/LazoCompanyService";
import WorkspaceService from "@/services/workspaces/WorkspaceService";
import { Divider } from "@mui/material";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { HttpStatusCode } from "axios";
import { Dispatch, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CommonDropdown } from "../common/CommonDropdown";
import { ADD_COMPANY_WORKSPACE } from "../signup-components/company/utils";
import CommonButton from "../utils/CommonButton";
import LazoModal from "../utils/LazoModal";

interface LazoOneWorkspaceManagementProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  ownedWorkspaces: Workspace[];
  tenantMemberships: TenantMembership[];
}

export function LazoOneWorkspaceManagement(props: LazoOneWorkspaceManagementProps) {
  const { t } = useTranslation("navigation");
  const [loading, setLoading] = useState<boolean>(false);

  const [selectedMoveWs, setSelectedMoveWS] = useState<Workspace | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<LazoCompany | null>(null);
  const [selectedCreationWS, setSelectedCreationWS] = useState<Workspace | null>(null);

  const { triggerFetch } = useTenantMemberships();

  const navigate = useNavigate();

  const onClickMove = () => {
    if (!selectedMoveWs || !selectedCompany) {
      return;
    }

    setLoading(true);
    WorkspaceService.changeRequest({ lazoCompanyExternalId: selectedCompany.externalId, selectedWorkspace: selectedMoveWs?.slug })
      .then((r) => {
        r.onSuccess(() => {
          NotificationService.info(t("workspaceManagement.workspaceChangedSuccess"));
          triggerFetch();
          props.setOpen(false);
        });
        r.onError((c) => {
          if (r.getHttpStatusCode() === HttpStatusCode.Unauthorized) {
            NotificationService.warn(c);
          } else {
            r.notifyErrorIfPresent();
          }
        });
      })
      .finally(() => setLoading(false));
  };

  function onMembershipSelection(t: string) {
    LazoCompanyService.getByTenantExternalId(t).then((r) => {
      r.onSuccess((c) => {
        setSelectedCompany(c);
      });
      r.notifyErrorIfPresent();
    });
  }

  return (
    <>
      <LazoModal title={t("workspaceManagement.title")} open={props.open} handleClose={() => props.setOpen(false)}>
        <Box display="flex" flexDirection="column" gap="1rem" width="100%">
          <Typography fontWeight="600">{t("workspaceManagement.moveEntityDescription")}</Typography>
          <Box display="flex" alignItems="center" gap="0.5rem" width="100%">
            <Typography>{t("workspaceManagement.move")}</Typography>
            <CommonDropdown
              border
              onChange={onMembershipSelection}
              placeholder={t("workspaceManagement.profile")}
              sx={{ flex: 1 }}
              customValueRender={<Typography>{selectedCompany?.legalName || ""}</Typography>}
              disabled={props.tenantMemberships.length === 0}
              currentValue={selectedCompany?.externalId || null}
              options={props.tenantMemberships.map((tm) => {
                return { render: <>{tm.tenant.name}</>, value: tm.tenant.externalId };
              })}
              name={"workspace-selector"}
            />
            <Typography>{t("workspaceManagement.to")}</Typography>
            <CommonDropdown
              border
              onChange={(slug) => {
                setSelectedMoveWS(props.ownedWorkspaces.find((ow) => ow.slug === slug) || null);
              }}
              placeholder={t("workspaceManagement.workspace")}
              sx={{ flex: 1 }}
              customValueRender={<Typography>{selectedMoveWs?.slug || ""}</Typography>}
              currentValue={selectedMoveWs?.slug || null}
              disabled={props.ownedWorkspaces.length === 0}
              options={props.ownedWorkspaces.map((ow) => {
                return { render: <>{ow.slug}</>, value: ow.slug };
              })}
              name={"workspace-selector"}
            />
          </Box>
          <CommonButton disabled={loading || !!!selectedMoveWs} onClick={onClickMove} sx={{ borderRadius: "0.5rem", width: "100%" }}>
            {loading ? <Spinner size="1rem" /> : <Typography>{t("workspaceManagement.move")}</Typography>}
          </CommonButton>
        </Box>
        <Divider />
        <Box display="flex" flexDirection="column" gap="1rem" width="100%">
          <Typography fontWeight="600">{t("workspaceManagement.createProfileDescription")}</Typography>
          <Box display="flex" alignItems="center" gap="0.5rem" width="100%">
            <Typography>{t("workspaceManagement.createInside")}</Typography>
            <CommonDropdown
              border
              onChange={(slug) => {
                setSelectedCreationWS(props.ownedWorkspaces.find((ow) => ow.slug === slug) || null);
              }}
              placeholder={t("workspaceManagement.workspace")}
              customValueRender={<Typography>{selectedCreationWS?.slug || ""}</Typography>}
              currentValue={selectedCreationWS?.slug || null}
              disabled={props.ownedWorkspaces.length === 0}
              options={props.ownedWorkspaces.map((ow) => {
                return { render: <>{ow.slug}</>, value: ow.slug };
              })}
              name={"workspace-selector"}
            />
          </Box>
          <CommonButton
            onClick={() => {
              localStorage.setItem(ADD_COMPANY_WORKSPACE, selectedCreationWS.slug);
              navigate(LazoPaths.ONE_SIGNUP);
              props.setOpen(false);
            }}
            sx={{ borderRadius: "0.5rem", width: "100%" }}
            disabled={!!!selectedCreationWS}
          >
            <Typography>{t("workspaceManagement.createProfile")}</Typography>
          </CommonButton>
        </Box>
        <Divider />
        <Box display="flex" flexDirection="column" gap="1rem" width="100%">
          <Typography fontWeight="600">{t("workspaceManagement.createWorkspaceDescription")}</Typography>
          <CommonButton
            sx={{ borderRadius: "0.5rem", width: "100%" }}
            onClick={() => {
              navigate(LazoPaths.ONE_SIGNUP);
              props.setOpen(false);
            }}
          >
            <Typography>{t("workspaceManagement.createWorkspace")}</Typography>
          </CommonButton>
        </Box>
      </LazoModal>
    </>
  );
}
